import React, { useEffect, useState } from "react";
import {
  collection,
  query,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  Timestamp,
  getDoc
} from "firebase/firestore";
import { db } from "./firebase";
import { useNavigate } from "react-router-dom";
import { useAdmin } from "./hooks/useAdmin";

// Expected categories for each discipline in our ranking systems
const expectedCategories = {
  "Latin": ["Solo Juvenile I", "Solo Juvenile II", "Solo Junior I", "Solo Junior II", "Solo Youth", "Solo Adult"],
  "Contemporary": ["Solo Contemporary Juvenile I", "Solo Contemporary Juvenile II", "Solo Contemporary Junior I", "Solo Contemporary Junior II", "Solo Contemporary Youth", "Solo Contemporary Adult"],
  "Hip Hop": ["Solo Hip Hop Juvenile I", "Solo Hip Hop Juvenile II", "Solo Hip Hop Junior I", "Solo Hip Hop Junior II", "Solo Hip Hop Youth", "Solo Hip Hop Adult"]
};

const expectedDances = {
  "Latin": ["Samba", "ChaChaCha", "Rumba", "Pasodoble", "Jive"],
  "Contemporary": ["Contemporary"],
  "Hip Hop": ["Hip Hop"]
};

// Function to validate if competition data complies with our ranking systems
const validateCompetitionData = (comp) => {
  const issues = [];
  
  // Check if discipline is recognized
  if (!expectedCategories[comp.discipline]) {
    issues.push(`Unknown discipline: ${comp.discipline}`);
  }
  
  // Check if category matches expected format
  if (comp.discipline && expectedCategories[comp.discipline]) {
    if (!expectedCategories[comp.discipline].includes(comp.category)) {
      issues.push(`Non-standard category: ${comp.category} for ${comp.discipline}`);
    }
  }
  
  // Check if dance field is appropriate
  if (comp.discipline && expectedDances[comp.discipline]) {
    if (comp.dance && !expectedDances[comp.discipline].includes(comp.dance)) {
      issues.push(`Non-standard dance: ${comp.dance} for ${comp.discipline}`);
    }
    
    // Latin should have dance field, others should match discipline
    if (comp.discipline === "Latin" && !comp.dance) {
      issues.push("Missing dance field for Latin competition");
    }
    if (comp.discipline !== "Latin" && comp.dance && comp.dance !== comp.discipline) {
      issues.push(`Dance field "${comp.dance}" doesn't match discipline "${comp.discipline}"`);
    }
  }
  
  // Check if results have proper structure
  if (!comp.results || !Array.isArray(comp.results) || comp.results.length === 0) {
    issues.push("Missing or empty results");
  } else {
    // Check if results have required fields
    const hasInvalidResults = comp.results.some(result => 
      !result.dancerName || 
      typeof result.points !== 'number' || 
      result.points < 0
    );
    if (hasInvalidResults) {
      issues.push("Invalid result entries (missing dancer name or invalid points)");
    }
  }
  
  return {
    isValid: issues.length === 0,
    issues: issues
  };
};

export default function CompetitionArchive() {
  const navigate = useNavigate();
  const { isAdmin } = useAdmin();
  const [loading, setLoading] = useState(true);
  const [competitions, setCompetitions] = useState([]);
  const [filteredCompetitions, setFilteredCompetitions] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedCompetition, setSelectedCompetition] = useState("");
  const [availableYears, setAvailableYears] = useState([]);
  const [availableCompetitions, setAvailableCompetitions] = useState([]);
  const [error, setError] = useState(null);
  
  // JSON Editor Modal states
  const [showJsonEditor, setShowJsonEditor] = useState(false);
  const [selectedCompetitionData, setSelectedCompetitionData] = useState(null);
  const [jsonContent, setJsonContent] = useState("");
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateStatus, setUpdateStatus] = useState("");
  const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });

  // Delete confirmation states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { type: 'competition' | 'category', compId, category?, structure }
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Missing categories modal states
  const [showMissingModal, setShowMissingModal] = useState(false);
  const [missingCategoriesData, setMissingCategoriesData] = useState(null);

  // Fetch all competition data
  useEffect(() => {
    const loadCompetitionData = async () => {
      setLoading(true);
      setError(null);

      try {
        const competitionData = [];

        // Query old structure
        console.log("🔍 Fetching competition archive from old structure...");
        const oldQuery = query(collection(db, "competitions"));
        const oldSnapshot = await getDocs(oldQuery);
        
        oldSnapshot.forEach((doc) => {
          const comp = doc.data();
          const compId = doc.id;
          const compName = comp.name || compId;
          
          if (comp.timestamp) {
            const compDate = comp.timestamp instanceof Timestamp ? comp.timestamp.toDate() : new Date(comp.timestamp);
            const year = compDate.getFullYear();
            
            // Get unique disciplines and categories from results
            const disciplinesAndCategories = new Set();
            if (comp.results && Array.isArray(comp.results)) {
              comp.results.forEach(result => {
                const discipline = comp.discipline || "Latin";
                const category = comp.category || "-";
                const dance = comp.dance || null;
                
                if (discipline === "Latin" && dance) {
                  disciplinesAndCategories.add(`${discipline} - ${category} (${dance})`);
                } else {
                  disciplinesAndCategories.add(`${discipline} - ${category}`);
                }
              });
            }

            competitionData.push({
              id: compId,
              name: compName,
              date: compDate,
              year: year,
              timestamp: comp.timestamp,
              discipline: comp.discipline || "Latin",
              category: comp.category || "-",
              dance: comp.dance || null,
              disciplinesAndCategories: Array.from(disciplinesAndCategories),
              participantCount: comp.results ? comp.results.length : 0,
              multiplier: comp.multiplier || 1,
              structure: "old",
              validation: validateCompetitionData(comp),
              results: comp.results // Include results for validation
            });
          }
        });

        // Query new structure
        console.log("🔍 Fetching competition archive from new structure...");
        const disciplinesSnapshot = await getDocs(collection(db, "disciplines"));
        
        for (const disciplineDoc of disciplinesSnapshot.docs) {
          const disciplineName = disciplineDoc.id;
          console.log("🔍 Checking discipline:", disciplineName);
          
          const categoriesSnapshot = await getDocs(collection(db, "disciplines", disciplineName, "categories"));
          
          for (const categoryDoc of categoriesSnapshot.docs) {
            const categoryName = categoryDoc.id;
            console.log("🔍 Checking category:", categoryName);
            
            // Check for direct competitions (non-Latin disciplines)
            try {
              const directCompsQuery = query(collection(db, "disciplines", disciplineName, "categories", categoryName, "competitions"));
              const directCompsSnapshot = await getDocs(directCompsQuery);
              
              directCompsSnapshot.forEach((doc) => {
                const comp = doc.data();
                const compId = doc.id;
                const compName = comp.name || compId;
                
                if (comp.timestamp) {
                  const compDate = comp.timestamp instanceof Timestamp ? comp.timestamp.toDate() : new Date(comp.timestamp);
                  const year = compDate.getFullYear();
                  
                  // Convert category name back to readable format
                  const readableCategory = categoryName.split("_").map(word => 
                    word.charAt(0).toUpperCase() + word.slice(1)
                  ).join(" ");
                  
                  const disciplinesAndCategories = [`${disciplineName} - ${readableCategory}`];

                  competitionData.push({
                    id: compId,
                    name: compName,
                    date: compDate,
                    year: year,
                    timestamp: comp.timestamp,
                    discipline: disciplineName,
                    category: readableCategory,
                    dance: null,
                    disciplinesAndCategories,
                    participantCount: comp.results ? comp.results.length : 0,
                    multiplier: comp.multiplier || 1,
                    structure: "new-direct",
                    validation: validateCompetitionData({...comp, discipline: disciplineName, category: readableCategory}),
                    results: comp.results
                  });
                }
              });
            } catch (error) {
              console.log("🔍 No direct competitions for", disciplineName, categoryName);
            }
            
            // Check for dance-specific competitions (Latin discipline)
            try {
              const dancesSnapshot = await getDocs(collection(db, "disciplines", disciplineName, "categories", categoryName, "dances"));
              
              for (const danceDoc of dancesSnapshot.docs) {
                const danceName = danceDoc.id;
                console.log("🔍 Checking dance:", danceName);
                
                const danceCompsQuery = query(collection(db, "disciplines", disciplineName, "categories", categoryName, "dances", danceName, "competitions"));
                const danceCompsSnapshot = await getDocs(danceCompsQuery);
                
                danceCompsSnapshot.forEach((doc) => {
                  const comp = doc.data();
                  const compId = doc.id;
                  const compName = comp.name || compId;
                  
                  if (comp.timestamp) {
                    const compDate = comp.timestamp instanceof Timestamp ? comp.timestamp.toDate() : new Date(comp.timestamp);
                    const year = compDate.getFullYear();
                    
                    // Convert category and dance names back to readable format
                    const readableCategory = categoryName.split("_").map(word => 
                      word.charAt(0).toUpperCase() + word.slice(1)
                    ).join(" ");
                    
                    const readableDance = danceName.charAt(0).toUpperCase() + danceName.slice(1);
                    
                    const disciplinesAndCategories = [`${disciplineName} - ${readableCategory} (${readableDance})`];

                    competitionData.push({
                      id: compId,
                      name: compName,
                      date: compDate,
                      year: year,
                      timestamp: comp.timestamp,
                      discipline: disciplineName,
                      category: readableCategory,
                      dance: readableDance,
                      disciplinesAndCategories,
                      participantCount: comp.results ? comp.results.length : 0,
                      multiplier: comp.multiplier || 1,
                      structure: "new-dance",
                      validation: validateCompetitionData({...comp, discipline: disciplineName, category: readableCategory, dance: readableDance}),
                      results: comp.results
                    });
                  }
                });
              }
            } catch (error) {
              console.log("🔍 No dances for", disciplineName, categoryName);
            }
          }
        }

        // Sort competitions by date (newest first)
        competitionData.sort((a, b) => b.date - a.date);

        // Get unique years and competition names for filters
        const years = [...new Set(competitionData.map(comp => comp.year))].sort((a, b) => b - a);
        const competitionNames = [...new Set(competitionData.map(comp => comp.name))].sort();

        console.log(`✅ Found ${competitionData.length} competitions across ${years.length} years`);

        setCompetitions(competitionData);
        setFilteredCompetitions(competitionData);
        setAvailableYears(years);
        setAvailableCompetitions(competitionNames);

      } catch (err) {
        console.error("❌ Error loading competition archive:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadCompetitionData();
  }, []);

  // Filter competitions based on selected filters
  useEffect(() => {
    let filtered = competitions;

    if (selectedYear) {
      filtered = filtered.filter(comp => comp.year === parseInt(selectedYear));
    }

    if (selectedCompetition) {
      filtered = filtered.filter(comp => comp.name === selectedCompetition);
    }

    setFilteredCompetitions(filtered);
  }, [selectedYear, selectedCompetition, competitions]);

  // Handle competition click to navigate to competition details
  const handleCompetitionClick = (competitionId) => {
    navigate(`/competition/${encodeURIComponent(competitionId)}`);
  };

  // Delete functions
  const handleDeleteCompetition = (comp) => {
    // comp may be a grouped competition object (has entries[]) or a raw entry
    const entry = comp.entries?.[0] ?? comp;
    setDeleteTarget({
      type: 'competition',
      compId: entry.id || comp.id,
      compName: comp.name,
      structure: entry.structure,
      discipline: entry.discipline,
      category: entry.category,
      dance: entry.dance,
    });
    setShowDeleteConfirm(true);
  };

  const handleDeleteCategory = (comp, category) => {
    setDeleteTarget({
      type: 'category',
      compId: comp.id,
      compName: comp.name,
      category: category,
      structure: comp.structure
    });
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    
    setDeleteLoading(true);
    try {
      if (deleteTarget.type === 'competition') {
        // Delete entire competition
        if (deleteTarget.structure === 'old') {
          await deleteDoc(doc(db, "competitions", deleteTarget.compId));
        } else {
          // For new structure, need to delete from disciplines path
          const disciplinePath = deleteTarget.discipline;
          const categoryPath = (deleteTarget.category || "").toLowerCase().replace(/ /g, "_");
          
          if (deleteTarget.dance) {
            // Dance-specific competition
            await deleteDoc(doc(db, "disciplines", disciplinePath, "categories", categoryPath, "dances", deleteTarget.dance.toLowerCase(), "competitions", deleteTarget.compId));
          } else {
            // Direct competition
            await deleteDoc(doc(db, "disciplines", disciplinePath, "categories", categoryPath, "competitions", deleteTarget.compId));
          }
        }
        
        // Remove from local state
        setCompetitions(prev => prev.filter(c => c.id !== deleteTarget.compId));
        setFilteredCompetitions(prev => prev.filter(c => c.id !== deleteTarget.compId));
        
      } else if (deleteTarget.type === 'category') {
        // Delete specific category from competition
        const comp = competitions.find(c => c.id === deleteTarget.compId);
        if (comp && comp.structure === 'old') {
          // For old structure, we can't easily delete just one category
          // This would require more complex logic to split the competition
          alert("Category deletion is not supported for old structure competitions. Please use the JSON editor to modify results.");
          return;
        }
      }
      
      setShowDeleteConfirm(false);
      setDeleteTarget(null);
      
    } catch (error) {
      console.error("Error deleting:", error);
      alert(`Error deleting: ${error.message}`);
    } finally {
      setDeleteLoading(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeleteTarget(null);
  };

  // Handle checking missing categories
  const handleCheckMissing = async (comp) => {
    try {
      console.log("Checking missing categories for competition:", comp.name);
      console.log("Competition entries:", comp.entries);
      
      // Analyze what categories are actually uploaded for this competition
      const uploadedByDiscipline = {};
      
      // Process each entry to see what's uploaded
      comp.entries.forEach(entry => {
        const discipline = entry.discipline;
        const category = entry.category;
        const dance = entry.dance;
        
        if (!uploadedByDiscipline[discipline]) {
          uploadedByDiscipline[discipline] = new Set();
        }
        
        // For Latin discipline, track categories by dance
        if (discipline === "Latin" && dance) {
          uploadedByDiscipline[discipline].add(`${category} (${dance})`);
        } else {
          // For other disciplines, just track the category
          uploadedByDiscipline[discipline].add(category);
        }
      });
      
      console.log("Uploaded by discipline:", uploadedByDiscipline);
      
      // Calculate missing categories
      const missingByDiscipline = {};
      let totalMissing = 0;
      let totalUploaded = 0;
      
      // Check each discipline that appears in this competition
      Object.keys(uploadedByDiscipline).forEach(discipline => {
        const uploaded = Array.from(uploadedByDiscipline[discipline]);
        totalUploaded += uploaded.length;
        
        console.log(`Analyzing ${discipline}:`, uploaded);
        
        if (expectedCategories[discipline]) {
          const missing = [];
          
          if (discipline === "Latin") {
            // For Latin, check each category-dance combination
            expectedCategories[discipline].forEach(expectedCategory => {
              expectedDances[discipline].forEach(dance => {
                const expectedCombination = `${expectedCategory} (${dance})`;
                if (!uploaded.includes(expectedCombination)) {
                  missing.push(expectedCombination);
                }
              });
            });
          } else {
            // For Contemporary and Hip Hop, check categories directly
            expectedCategories[discipline].forEach(expectedCategory => {
              if (!uploaded.includes(expectedCategory)) {
                missing.push(expectedCategory);
              }
            });
          }
          
          if (missing.length > 0) {
            missingByDiscipline[discipline] = missing;
            totalMissing += missing.length;
          }
          
          console.log(`${discipline} missing:`, missing);
        }
      });
      
      console.log("Final missing analysis:", missingByDiscipline);
      console.log("Total missing:", totalMissing, "Total uploaded:", totalUploaded);

      // Prepare modal data
      setMissingCategoriesData({
        competition: comp,
        totalUploaded,
        totalMissing,
        missingByDiscipline,
        uploadedCategories: Object.values(uploadedByDiscipline).reduce((all, cats) => {
          return all.concat(Array.from(cats));
        }, [])
      });
      
      setShowMissingModal(true);
      
    } catch (error) {
      console.error("Error checking missing categories:", error);
      alert(`Error checking missing categories: ${error.message}`);
    }
  };

  // Handle category click to open JSON editor
  const handleCategoryClick = async (entry, event) => {
    try {
      // Capture mouse position for modal positioning
      const rect = event.target.getBoundingClientRect();
      setModalPosition({
        x: event.clientX,
        y: event.clientY
      });
      
      setUpdateStatus("Loading competition data...");
      
      // Fetch the full competition data based on the structure type
      let competitionData = null;
      let docPath = "";
      
      if (entry.structure === "old") {
        // Old structure: competitions/{id}
        const docRef = doc(db, "competitions", entry.id);
        const docSnap = await getDocs(query(collection(db, "competitions")));
        
        docSnap.forEach((doc) => {
          if (doc.id === entry.id) {
            competitionData = { id: doc.id, ...doc.data() };
            docPath = `competitions/${entry.id}`;
          }
        });
      } else if (entry.structure === "new-direct") {
        // New structure direct: disciplines/{discipline}/categories/{category}/competitions/{id}
        const disciplineName = entry.discipline.toLowerCase().replace(/\s+/g, "_");
        const categoryName = entry.category.toLowerCase().replace(/\s+/g, "_");
        docPath = `disciplines/${disciplineName}/categories/${categoryName}/competitions/${entry.id}`;
        
        const docRef = doc(db, "disciplines", disciplineName, "categories", categoryName, "competitions", entry.id);
        const docSnap = await getDocs(query(collection(db, "disciplines", disciplineName, "categories", categoryName, "competitions")));
        
        docSnap.forEach((doc) => {
          if (doc.id === entry.id) {
            competitionData = { id: doc.id, ...doc.data() };
          }
        });
      } else if (entry.structure === "new-dance") {
        // New structure with dance: disciplines/{discipline}/categories/{category}/dances/{dance}/competitions/{id}
        const disciplineName = entry.discipline.toLowerCase().replace(/\s+/g, "_");
        const categoryName = entry.category.toLowerCase().replace(/\s+/g, "_");
        const danceName = entry.dance.toLowerCase().replace(/[^a-z0-9]+/g, "_");
        docPath = `disciplines/${disciplineName}/categories/${categoryName}/dances/${danceName}/competitions/${entry.id}`;
        
        const docRef = doc(db, "disciplines", disciplineName, "categories", categoryName, "dances", danceName, "competitions", entry.id);
        const docSnap = await getDocs(query(collection(db, "disciplines", disciplineName, "categories", categoryName, "dances", danceName, "competitions")));
        
        docSnap.forEach((doc) => {
          if (doc.id === entry.id) {
            competitionData = { id: doc.id, ...doc.data() };
          }
        });
      }
      
      if (competitionData) {
        // Convert Timestamp objects to readable format for editing
        const editableData = { ...competitionData };
        if (editableData.timestamp && editableData.timestamp instanceof Timestamp) {
          editableData.timestamp = editableData.timestamp.toDate().toISOString();
        }
        
        setSelectedCompetitionData({
          ...entry,
          data: competitionData,
          docPath: docPath
        });
        setJsonContent(JSON.stringify(editableData, null, 2));
        setShowJsonEditor(true);
        setUpdateStatus("");
      } else {
        setUpdateStatus("❌ Failed to load competition data");
      }
    } catch (error) {
      console.error("Error loading competition data:", error);
      setUpdateStatus(`❌ Error: ${error.message}`);
    }
  };

  // Handle JSON update
  const handleJsonUpdate = async () => {
    if (!selectedCompetitionData) return;
    
    setUpdateLoading(true);
    setUpdateStatus("Updating competition...");
    
    try {
      // Parse and validate JSON
      const updatedData = JSON.parse(jsonContent);
      
      // Convert ISO string back to Timestamp if needed
      if (updatedData.timestamp && typeof updatedData.timestamp === 'string') {
        updatedData.timestamp = Timestamp.fromDate(new Date(updatedData.timestamp));
      }
      
      // Remove id from update data (Firestore doesn't allow updating the document ID)
      const { id, ...updateData } = updatedData;
      
      // Update the document based on structure
      const pathParts = selectedCompetitionData.docPath.split('/');
      const docRef = doc(db, ...pathParts);
      
      await updateDoc(docRef, updateData);
      
      setUpdateStatus("✅ Competition updated successfully!");
      
      // Refresh the competition list after update
      setTimeout(() => {
        setShowJsonEditor(false);
        window.location.reload(); // Simple reload to refresh data
      }, 1500);
      
    } catch (error) {
      console.error("Error updating competition:", error);
      setUpdateStatus(`❌ Update failed: ${error.message}`);
    } finally {
      setUpdateLoading(false);
    }
  };

  // Format date for display
  const formatDate = (date) => {
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric"
    });
  };

  // Group competitions by name to show all disciplines/categories for each competition
  const getGroupedCompetitions = () => {
    const grouped = {};
    
    filteredCompetitions.forEach(comp => {
      if (!grouped[comp.name]) {
        grouped[comp.name] = {
          name: comp.name,
          id: comp.id, // Use first ID found
          date: comp.date,
          year: comp.year,
          entries: [],
          totalParticipants: 0,
          structures: new Set()
        };
      }
      
      // Compute docPath based on structure
      let docPath = "";
      if (comp.structure === "old") {
        docPath = `competitions/${comp.id}`;
      } else if (comp.structure === "new-direct") {
        const disciplineName = comp.discipline.toLowerCase().replace(/\s+/g, "_");
        const categoryName = comp.category.toLowerCase().replace(/\s+/g, "_");
        docPath = `disciplines/${disciplineName}/categories/${categoryName}/competitions/${comp.id}`;
      } else if (comp.structure === "new-dance") {
        const disciplineName = comp.discipline.toLowerCase().replace(/\s+/g, "_");
        const categoryName = comp.category.toLowerCase().replace(/\s+/g, "_");
        const danceName = comp.dance.toLowerCase().replace(/[^a-z0-9]+/g, "_");
        docPath = `disciplines/${disciplineName}/categories/${categoryName}/dances/${danceName}/competitions/${comp.id}`;
      }
      
      grouped[comp.name].entries.push({
        discipline: comp.discipline,
        category: comp.category,
        dance: comp.dance,
        participantCount: comp.participantCount,
        multiplier: comp.multiplier,
        structure: comp.structure,
        id: comp.id,
        validation: comp.validation,
        results: comp.results,
        docPath: docPath
      });
      
      grouped[comp.name].totalParticipants += comp.participantCount;
      grouped[comp.name].structures.add(comp.structure);
    });

    // Convert structures set to array for display
    Object.values(grouped).forEach(comp => {
      comp.structures = Array.from(comp.structures);
    });

    return Object.values(grouped).sort((a, b) => b.date - a.date);
  };

  if (loading) return <div style={{ padding: "2rem" }}><p>Loading competition archive...</p></div>;
  if (error) return <div style={{ padding: "2rem" }}><p>Error: {error}</p></div>;

  const groupedCompetitions = getGroupedCompetitions();
  
  // Calculate validation summary
  const validationSummary = filteredCompetitions.reduce((summary, comp) => {
    if (comp.validation) {
      if (comp.validation.isValid) {
        summary.valid++;
      } else {
        summary.invalid++;
        comp.validation.issues.forEach(issue => {
          summary.issueTypes[issue] = (summary.issueTypes[issue] || 0) + 1;
        });
      }
    }
    summary.total++;
    return summary;
  }, { valid: 0, invalid: 0, total: 0, issueTypes: {} });

  return (
    <div style={{ padding: "1rem" }}>
      <h2>📁 Competition Archive</h2>
      <p style={{ marginBottom: "2rem", color: "#666" }}>
        Browse all uploaded competition results by year and competition name. Click on any competition to view detailed results.
      </p>

      <div style={{
        padding: "1rem",
        marginBottom: "1rem",
        backgroundColor: validationSummary.invalid > 0 ? "#fff3cd" : "#d4edda",
        border: `1px solid ${validationSummary.invalid > 0 ? "#ffc107" : "#28a745"}`,
        borderRadius: "8px"
      }}>
        <h3 style={{ margin: "0 0 0.5rem 0", color: "#495057" }}>
          📋 Competition Data Validation Status
        </h3>
        <div style={{ display: "flex", gap: "2rem", flexWrap: "wrap", alignItems: "center" }}>
          <div>
            <strong>✅ Valid: {validationSummary.valid}</strong>
          </div>
          <div>
            <strong style={{ color: validationSummary.invalid > 0 ? "#dc3545" : "#28a745" }}>
              {validationSummary.invalid > 0 ? "❌" : "✅"} Invalid: {validationSummary.invalid}
            </strong>
          </div>
          <div>
            <strong>📊 Total: {validationSummary.total}</strong>
          </div>
        </div>
        {validationSummary.invalid > 0 && (
          <div style={{ marginTop: "0.5rem" }}>
            <p style={{ margin: "0.5rem 0", fontSize: "0.9rem", color: "#666" }}>
              <strong>Common Issues:</strong>
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
              {Object.entries(validationSummary.issueTypes).map(([issue, count]) => (
                <span key={issue} style={{
                  backgroundColor: "#f8d7da",
                  border: "1px solid #dc3545",
                  padding: "0.25rem 0.5rem",
                  borderRadius: "4px",
                  fontSize: "0.8rem"
                }}>
                  {issue} ({count})
                </span>
              ))}
            </div>
            <p style={{ margin: "0.5rem 0 0 0", fontSize: "0.8rem", fontStyle: "italic", color: "#666" }}>
              Competitions with issues are marked with ❌ symbol. Hover over them to see specific problems.
            </p>
          </div>
        )}
      </div>

      {/* Filters */}
      <div style={{ 
        display: "flex", 
        gap: "1rem", 
        marginBottom: "2rem", 
        flexWrap: "wrap",
        padding: "1rem",
        backgroundColor: "#f5f5f5",
        borderRadius: "8px"
      }}>
        <div>
          <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "bold" }}>
            Filter by Year:
          </label>
          <select 
            value={selectedYear} 
            onChange={(e) => {
              setSelectedYear(e.target.value);
              setSelectedCompetition(""); // Reset competition filter when year changes
            }}
            style={{ padding: "0.5rem", borderRadius: "4px", border: "1px solid #ccc", minWidth: "120px" }}
          >
            <option value="">All Years</option>
            {availableYears.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>

        <div>
          <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "bold" }}>
            Filter by Competition:
          </label>
          <select 
            value={selectedCompetition} 
            onChange={(e) => setSelectedCompetition(e.target.value)}
            style={{ padding: "0.5rem", borderRadius: "4px", border: "1px solid #ccc", minWidth: "200px" }}
          >
            <option value="">All Competitions</option>
            {availableCompetitions
              .filter(compName => {
                // Only show competitions that exist in the selected year
                if (!selectedYear) return true;
                return competitions.some(comp => 
                  comp.name === compName && comp.year === parseInt(selectedYear)
                );
              })
              .map(compName => (
                <option key={compName} value={compName}>{compName}</option>
              ))}
          </select>
        </div>

        <div style={{ display: "flex", alignItems: "end" }}>
          <button
            onClick={() => {
              setSelectedYear("");
              setSelectedCompetition("");
            }}
            style={{
              padding: "0.5rem 1rem",
              backgroundColor: "#6c757d",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer"
            }}
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Results Summary */}
      <div style={{ marginBottom: "1rem", padding: "0.75rem", backgroundColor: "#e3f2fd", borderRadius: "4px" }}>
        <strong>📊 Results:</strong> Showing {groupedCompetitions.length} competitions
        {selectedYear && ` from ${selectedYear}`}
        {selectedCompetition && ` matching "${selectedCompetition}"`}
      </div>

      {/* Competition List */}
      {groupedCompetitions.length === 0 ? (
        <div style={{ textAlign: "center", padding: "2rem", color: "#666" }}>
          <p>No competitions found matching the selected filters.</p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          {groupedCompetitions.map((comp, index) => (
            <div key={index} style={{
              border: "1px solid #ddd",
              borderRadius: "8px",
              padding: "1rem",
              backgroundColor: "white",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1rem" }}>
                <div>
                  <h3 style={{ 
                    margin: "0 0 0.5rem 0", 
                    color: "#007bff",
                    cursor: "pointer",
                    textDecoration: "underline"
                  }}
                  onClick={() => handleCompetitionClick(comp.id)}
                  >
                    {comp.name}
                  </h3>
                  <p style={{ margin: "0", color: "#666", fontSize: "0.9rem" }}>
                    📅 {formatDate(comp.date)} • 👥 {comp.totalParticipants} total participants
                  </p>
                </div>
                <div style={{ textAlign: "right", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "0.5rem" }}>
                  <span style={{ 
                    backgroundColor: "#e9ecef", 
                    padding: "0.25rem 0.5rem", 
                    borderRadius: "4px", 
                    fontSize: "0.8rem",
                    color: "#495057"
                  }}>
                    {comp.year}
                  </span>
                  {isAdmin && (
                    <>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCheckMissing(comp);
                        }}
                        style={{
                          backgroundColor: "#17a2b8",
                          color: "white",
                          border: "none",
                          padding: "0.25rem 0.5rem",
                          borderRadius: "4px",
                          fontSize: "0.75rem",
                          cursor: "pointer",
                          transition: "background-color 0.2s",
                          marginBottom: "0.25rem"
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "#138496"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "#17a2b8"}
                        title="Check missing categories that need to be uploaded"
                      >
                        📋 Check Missing
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteCompetition(comp);
                        }}
                        style={{
                          backgroundColor: "#dc3545",
                          color: "white",
                          border: "none",
                          padding: "0.25rem 0.5rem",
                          borderRadius: "4px",
                          fontSize: "0.75rem",
                          cursor: "pointer",
                          transition: "background-color 0.2s"
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "#c82333"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "#dc3545"}
                        title="Delete entire competition"
                      >
                        🗑️ Delete
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div>
                <h4 style={{ margin: "0 0 0.5rem 0", fontSize: "1rem", color: "#495057" }}>
                  Disciplines & Categories:
                </h4>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                  {comp.entries.map((entry, entryIndex) => (
                    <span key={entryIndex}
                      onClick={(e) => handleCategoryClick(entry, e)}
                      style={{
                        backgroundColor: entry.discipline === "Latin" ? "#fff3cd" :
                          entry.discipline === "Contemporary" ? "#d4edda" : "#f8d7da",
                        border: `1px solid ${entry.discipline === "Latin" ? "#ffc107" :
                          entry.discipline === "Contemporary" ? "#28a745" : "#dc3545"}`,
                        padding: "0.25rem 0.5rem",
                        borderRadius: "4px",
                        fontSize: "0.85rem",
                        display: "inline-block",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        position: "relative"
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = "translateY(-1px)";
                        e.target.style.boxShadow = "0 2px 4px rgba(0,0,0,0.2)";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = "translateY(0)";
                        e.target.style.boxShadow = "none";
                      }}
                      title={entry.validation && !entry.validation.isValid ?
                        `❌ Validation Issues:\n${entry.validation.issues.join('\n')}` :
                        '✅ Compliant with ranking systems'}
                    >
                      {/* Render results for this category/discipline */}
                      {entry.results && entry.results.map((result, i) => (
                        <div key={i} style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "2px" }}>
                          <span style={{ fontWeight: "bold" }}>{result.place}</span>
                          {/* Withdrawn indicator for public */}
                          {result.withdrawn && (
                            <span style={{ color: "#dc3545", fontWeight: "bold", fontSize: "1.1em" }} title="Withdrawn">W</span>
                          )}
                          {/* Admin toggle for withdrawn */}
                          {isAdmin && (
                            <span
                              style={{
                                color: result.withdrawn ? "#dc3545" : "#28a745",
                                fontWeight: "bold",
                                fontSize: "1.1em",
                                cursor: "pointer",
                                marginLeft: "2px"
                              }}
                              title={result.withdrawn ? "Withdrawn (click to mark active)" : "Active (click to mark withdrawn)"}
                              onClick={async (e) => {
                                e.stopPropagation();
                                // Toggle withdrawn status in Firestore
                                try {
                                  const pathParts = entry.docPath.split('/');
                                  const docRef = doc(db, ...pathParts);
                                  const docSnap = await getDoc(docRef);
                                  if (docSnap.exists()) {
                                    const data = docSnap.data();
                                    const updatedResults = data.results.map((r, idx) => idx === i ? { ...r, withdrawn: !r.withdrawn } : r);
                                    await updateDoc(docRef, { results: updatedResults });
                                    window.location.reload();
                                  }
                                } catch (err) {
                                  alert("Failed to update withdrawn status: " + err.message);
                                }
                              }}
                            >
                              {result.withdrawn ? "W" : "✔"}
                            </span>
                          )}
                          <span>{result.dancerName}</span>
                        </div>
                      ))}
                      {/* ...existing code for validation badges, etc... */}
                      {entry.validation && !entry.validation.isValid && (
                        <span
                          style={{
                            position: "absolute",
                            top: "-8px",
                            right: "-8px",
                            backgroundColor: "#dc3545",
                            color: "white",
                            borderRadius: "50%",
                            width: "20px",
                            height: "20px",
                            fontSize: "14px",
                            fontWeight: "bold",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            border: "2px solid white",
                            cursor: "help"
                          }}
                          title={`❌ Issues:\n• ${entry.validation.issues.join('\n• ')}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            alert(`Validation Issues:\n\n• ${entry.validation.issues.join('\n• ')}`);
                          }}
                        >
                          ✕
                        </span>
                      )}
                      <strong>{entry.discipline}</strong> - {entry.category}
                      {entry.dance && ` (${entry.dance})`}
                      <span style={{ marginLeft: "0.5rem", color: "#666" }}>
                        [{entry.participantCount} participants]
                      </span>
                      {entry.multiplier !== 1 && (
                        <span style={{ marginLeft: "0.25rem", color: "#007bff", fontWeight: "bold" }}>
                          ×{entry.multiplier}
                        </span>
                      )}
                    </span>
                  ))}
                </div>
              </div>

              {comp.structures.length > 1 && (
                <div style={{ marginTop: "0.5rem", fontSize: "0.8rem", color: "#6c757d" }}>
                  <em>Data sources: {comp.structures.join(", ")}</em>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* JSON Editor Modal */}
      {showJsonEditor && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0,0,0,0.7)",
          zIndex: 1000,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "flex-start",
          padding: "1rem"
        }}>
          <div style={{
            backgroundColor: "white",
            borderRadius: "8px",
            padding: "2rem",
            maxWidth: "min(90vw, 800px)",
            maxHeight: "min(90vh, 600px)",
            overflow: "auto",
            position: "relative",
            minWidth: "600px",
            left: Math.min(modalPosition.x - 300, window.innerWidth - 650),
            top: Math.min(modalPosition.y - 100, window.innerHeight - 650),
            boxShadow: "0 20px 60px rgba(0,0,0,0.3)"
          }}>
            <h2 style={{ marginTop: 0, marginBottom: "1rem" }}>
              Edit Competition JSON
            </h2>
            
            {selectedCompetitionData && (
              <div style={{ marginBottom: "1rem", padding: "1rem", backgroundColor: "#f8f9fa", borderRadius: "4px" }}>
                <strong>Competition:</strong> {selectedCompetitionData.name}<br/>
                <strong>Category:</strong> {selectedCompetitionData.discipline} - {selectedCompetitionData.category}
                {selectedCompetitionData.dance && <><br/><strong>Dance:</strong> {selectedCompetitionData.dance}</>}
                <br/><strong>Path:</strong> <code>{selectedCompetitionData.docPath}</code>
              </div>
            )}
            
            <div style={{ marginBottom: "1rem" }}>
              <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "bold" }}>
                Competition JSON Data:
              </label>
              <textarea
                value={jsonContent}
                onChange={(e) => setJsonContent(e.target.value)}
                style={{
                  width: "100%",
                  height: "400px",
                  fontFamily: "monospace",
                  fontSize: "12px",
                  padding: "1rem",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  resize: "vertical"
                }}
                placeholder="JSON content will appear here..."
              />
            </div>
            
            {updateStatus && (
              <div style={{ 
                marginBottom: "1rem", 
                padding: "0.75rem", 
                borderRadius: "4px",
                backgroundColor: updateStatus.includes("✅") ? "#d4edda" : 
                                updateStatus.includes("❌") ? "#f8d7da" : "#fff3cd",
                border: `1px solid ${updateStatus.includes("✅") ? "#c3e6cb" : 
                                   updateStatus.includes("❌") ? "#f5c6cb" : "#ffeaa7"}`
              }}>
                {updateStatus}
              </div>
            )}
            
            <div style={{ display: "flex", gap: "1rem", justifyContent: "flex-end" }}>
              <button
                onClick={() => setShowJsonEditor(false)}
                style={{
                  padding: "0.75rem 1.5rem",
                  backgroundColor: "#6c757d",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer"
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleJsonUpdate}
                disabled={updateLoading}
                style={{
                  padding: "0.75rem 1.5rem",
                  backgroundColor: updateLoading ? "#ccc" : "#007bff",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: updateLoading ? "not-allowed" : "pointer"
                }}
              >
                {updateLoading ? "Updating..." : "Update Competition"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && deleteTarget && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: "white",
            padding: "2rem",
            borderRadius: "8px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
            maxWidth: "500px",
            width: "90%"
          }}>
            <h3 style={{ margin: "0 0 1rem 0", color: "#dc3545" }}>
              ⚠️ Confirm Deletion
            </h3>
            
            <p style={{ margin: "0 0 1.5rem 0", color: "#333" }}>
              {deleteTarget.type === 'competition' ? (
                <>
                  Are you sure you want to delete the entire competition <strong>"{deleteTarget.compName}"</strong>?
                  <br /><br />
                  <span style={{ color: "#dc3545", fontWeight: "bold" }}>
                    This action cannot be undone and will permanently remove all competition data including results for all disciplines and categories.
                  </span>
                </>
              ) : (
                <>
                  Are you sure you want to delete the category <strong>"{deleteTarget.category}"</strong> from competition <strong>"{deleteTarget.compName}"</strong>?
                  <br /><br />
                  <span style={{ color: "#dc3545", fontWeight: "bold" }}>
                    This action cannot be undone and will permanently remove all results for this category.
                  </span>
                </>
              )}
            </p>

            <div style={{ display: "flex", gap: "1rem", justifyContent: "flex-end" }}>
              <button
                onClick={cancelDelete}
                disabled={deleteLoading}
                style={{
                  padding: "0.75rem 1.5rem",
                  backgroundColor: "#6c757d",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: deleteLoading ? "not-allowed" : "pointer"
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleteLoading}
                style={{
                  padding: "0.75rem 1.5rem",
                  backgroundColor: deleteLoading ? "#ccc" : "#dc3545",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: deleteLoading ? "not-allowed" : "pointer"
                }}
              >
                {deleteLoading ? "Deleting..." : `Delete ${deleteTarget.type === 'competition' ? 'Competition' : 'Category'}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Missing Categories Modal */}
      {showMissingModal && missingCategoriesData && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: "white",
            borderRadius: "8px",
            padding: "2rem",
            maxWidth: "600px",
            maxHeight: "80vh",
            overflow: "auto",
            boxShadow: "0 20px 60px rgba(0,0,0,0.3)"
          }}>
            <h2 style={{ marginTop: 0, marginBottom: "1rem", color: "#17a2b8" }}>
              📋 Missing Categories Check
            </h2>
            
            <div style={{ marginBottom: "1.5rem", padding: "1rem", backgroundColor: "#f8f9fa", borderRadius: "4px" }}>
              <strong>Competition:</strong> {missingCategoriesData.competition.name}<br/>
              <strong>Date:</strong> {missingCategoriesData.competition.date ? 
                (typeof missingCategoriesData.competition.date === 'string' ? 
                  missingCategoriesData.competition.date : 
                  missingCategoriesData.competition.date.toLocaleDateString ? 
                    missingCategoriesData.competition.date.toLocaleDateString() : 
                    String(missingCategoriesData.competition.date)
                ) : 'Unknown'
              }<br/>
              <strong>Current Categories:</strong> {missingCategoriesData.totalUploaded}
            </div>

            <h3 style={{ color: "#495057", fontSize: "1.1rem", marginBottom: "1rem" }}>
              Categories That Need To Be Uploaded:
            </h3>

            {missingCategoriesData.totalMissing > 0 ? (
              <>
                <div style={{ 
                  backgroundColor: "#fff3cd", 
                  border: "1px solid #ffc107", 
                  borderRadius: "4px", 
                  padding: "0.75rem", 
                  marginBottom: "1rem" 
                }}>
                  📝 <strong>Upload Needed:</strong> {missingCategoriesData.totalMissing} categories are missing and need to be uploaded manually.
                </div>

                {Object.entries(missingCategoriesData.missingByDiscipline).map(([discipline, missing]) => (
                  <div key={discipline} style={{ marginBottom: "1rem" }}>
                    <h4 style={{ 
                      color: discipline === "Latin" ? "#ffc107" : 
                             discipline === "Contemporary" ? "#28a745" : "#dc3545",
                      fontSize: "1rem",
                      marginBottom: "0.5rem"
                    }}>
                      {discipline} ({missing.length} missing)
                    </h4>
                    <ul style={{ margin: "0 0 0 1rem", paddingLeft: "1rem" }}>
                      {missing.map((category, index) => (
                        <li key={index} style={{ marginBottom: "0.25rem", fontSize: "0.9rem" }}>
                          {category}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}

                <div style={{ 
                  backgroundColor: "#d4edda", 
                  border: "1px solid #28a745", 
                  borderRadius: "4px", 
                  padding: "0.75rem", 
                  marginTop: "1rem" 
                }}>
                  💡 <strong>Next Steps:</strong> Use the competition upload form to manually add these missing categories with their results.
                </div>
              </>
            ) : (
              <div style={{ 
                backgroundColor: "#d4edda", 
                border: "1px solid #28a745", 
                borderRadius: "4px", 
                padding: "0.75rem", 
                textAlign: "center" 
              }}>
                ✅ <strong>All categories uploaded!</strong> No missing categories detected for this competition.
              </div>
            )}
            
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "1.5rem" }}>
              <button
                onClick={() => setShowMissingModal(false)}
                style={{
                  padding: "0.75rem 1.5rem",
                  backgroundColor: "#6c757d",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer"
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
